import { createTheme } from '@mui/material/styles';
import { heIL } from '@mui/material/locale';

const getDesignTokens = (mode) => ({
  direction: 'rtl',
  palette: {
    mode,
    primary: {
      main: '#1565C0',
      light: '#1E88E5',
      dark: '#0D47A1',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#00897B',
      light: '#26A69A',
      dark: '#00695C',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
      bg: '#E8F5E9',
    },
    warning: {
      main: '#F57F17',
      light: '#FFB300',
      dark: '#E65100',
      bg: '#FFF8E1',
    },
    error: {
      main: '#C62828',
      light: '#EF5350',
      dark: '#B71C1C',
      bg: '#FFEBEE',
    },
    accent: {
      main: '#FF6F00',
      bg: mode === 'dark' ? '#FFF3E0' : '#FFF3E0',
    },
    gold: {
      main: '#F9A825',
      bg: '#FFF8E1',
    },
    background: {
      default: mode === 'dark' ? '#121212' : '#F5F5F5',
      paper: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
      card: mode === 'dark' ? '#2C2C2C' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#E0E0E0' : '#212121',
      secondary: mode === 'dark' ? '#BDBDBD' : '#757575',
    },
    divider: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
  },
  typography: {
    fontFamily: '"Rubik", "Inter", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    h1: { fontWeight: 700, fontSize: '2rem' },
    h2: { fontWeight: 700, fontSize: '1.75rem' },
    h3: { fontWeight: 600, fontSize: '1.5rem' },
    h4: { fontWeight: 600, fontSize: '1.25rem' },
    h5: { fontWeight: 500, fontSize: '1.1rem' },
    h6: { fontWeight: 500, fontSize: '1rem' },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem' },
    button: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    mode === 'dark'
      ? '0px 2px 4px rgba(0,0,0,0.4)'
      : '0px 2px 4px rgba(0,0,0,0.05)',
    mode === 'dark'
      ? '0px 4px 8px rgba(0,0,0,0.4)'
      : '0px 4px 8px rgba(0,0,0,0.08)',
    mode === 'dark'
      ? '0px 8px 16px rgba(0,0,0,0.5)'
      : '0px 8px 16px rgba(0,0,0,0.1)',
    ...Array(21).fill(mode === 'dark'
      ? '0px 8px 24px rgba(0,0,0,0.5)'
      : '0px 8px 24px rgba(0,0,0,0.12)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 20px',
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          textAlign: 'left', // stylis RTL plugin flips this to 'right'
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
  },
});

export const createAppTheme = (mode) => createTheme(getDesignTokens(mode), heIL);

export const PRIORITY_COLORS = {
  'גבוה': { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  'בינוני': { bg: '#FFF8E1', color: '#F57F17', border: '#FFE082' },
  'נמוך': { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
};

export const STATUS_COLORS = {
  'בבחינה': { bg: '#E3F2FD', color: '#1565C0' },
  'בהכנה': { bg: '#FFF3E0', color: '#FF6F00' },
  'הוגש': { bg: '#E8F5E9', color: '#2E7D32' },
};

export const RESULT_COLORS = {
  'ממתין להחלטה': { bg: '#FFF8E1', color: '#F57F17' },
  'זכייה': { bg: '#E8F5E9', color: '#2E7D32' },
  'אי-זכייה': { bg: '#FFEBEE', color: '#C62828' },
};

export default createAppTheme;
